/**
 * DefaultCalendarManager
 *
 * Manages the user's preferred default calendar for new event creation.
 * Per-user scoped storage (calendar IDs are device-specific, so no Supabase sync).
 *
 * Validates on read: if the stored ID is no longer in the writable calendars
 * list, returns null so callers fall back to their existing "pick first writable"
 * behavior.
 */

import type { DeviceCalendar } from '@/lib/calendar/DeviceCalendarService';
import { ScopedAsyncStorage } from '@/lib/storage';

const STORAGE_KEY = '@default_calendar_id';

export class DefaultCalendarManager {
  private readonly storage: ScopedAsyncStorage;

  constructor(storage: ScopedAsyncStorage) {
    this.storage = storage;
  }

  /**
   * Get the stored default calendar ID. Returns null if not set or if the
   * stored ID is not in the provided writable calendars list.
   */
  async getDefault(writableCalendars: DeviceCalendar[]): Promise<string | null> {
    try {
      const storedId = await this.storage.getItem(STORAGE_KEY);
      if (!storedId) {
        return null;
      }
      const isValid = writableCalendars.some((cal) => cal.id === storedId);
      return isValid ? storedId : null;
    } catch (err) {
      console.error('[DefaultCalendarManager:getDefault] Failed to load:', err);
      return null;
    }
  }

  /**
   * Get the raw stored default calendar ID without validation.
   * Used by settings UI to show what is currently selected.
   */
  async getStoredId(): Promise<string | null> {
    try {
      return await this.storage.getItem(STORAGE_KEY);
    } catch (err) {
      console.error('[DefaultCalendarManager:getStoredId] Failed to load:', err);
      return null;
    }
  }

  /**
   * Persist the selected default calendar ID.
   */
  async setDefault(calendarId: string): Promise<void> {
    try {
      await this.storage.setItem(STORAGE_KEY, calendarId);
    } catch (err) {
      console.error('[DefaultCalendarManager:setDefault] Failed to save:', err);
      throw new Error('Failed to save default calendar preference');
    }
  }

  /**
   * Clear the stored default calendar preference.
   */
  async clearDefault(): Promise<void> {
    try {
      await this.storage.removeItem(STORAGE_KEY);
    } catch (err) {
      console.error('[DefaultCalendarManager:clearDefault] Failed to clear:', err);
    }
  }
}
